/**
 * Typed API client. Paths, parameters and responses come from FastAPI's
 * OpenAPI schema (`npm run gen:api` writes src/api/schema.d.ts), so
 *
 *   const data = await api.get("/market/overview", { query: { date: "2026-10-21" } });
 *
 * is checked end to end. The access token is attached automatically; on a 401
 * the client refreshes once and retries.
 */
import { API_BASE } from "./config";
import { ApiError, errorFrom } from "./errors";
import { takePrefetched } from "./prefetch";
import { getAccessToken, getView, hasRefreshToken, refreshAccess } from "./session";
import type { paths } from "./schema";
import { VIEW_ONLY_EVENT } from "./viewStore";

export type Method = "get" | "post" | "put" | "patch" | "delete";

/** Paths that support a method, e.g. `PathsFor<"get">`. */
export type PathsFor<M extends Method> = {
  [P in keyof paths]: paths[P][M] extends { responses: unknown } ? P : never;
}[keyof paths];

type Op<P extends keyof paths, M extends Method> = NonNullable<paths[P][M]>;

type QueryOf<O> = O extends { parameters: { query?: infer Q } } ? NonNullable<Q> : never;
type PathOf<O> = O extends { parameters: { path: infer X } } ? NonNullable<X> : never;
type BodyOf<O> = O extends { requestBody: { content: { "application/json": infer B } } }
  ? B
  : O extends { requestBody?: { content: { "application/json": infer B } } }
    ? B
    : never;

type Json<R> = R extends { content: { "application/json": infer T } } ? T : never;
export type ResponseOf<O> = O extends { responses: infer R }
  ? 200 extends keyof R
    ? Json<R[200]>
    : 201 extends keyof R
      ? Json<R[201]>
      : undefined
  : never;

export type RequestOptions<O> = {
  query?: [QueryOf<O>] extends [never] ? never : QueryOf<O>;
  body?: [BodyOf<O>] extends [never] ? never : BodyOf<O>;
  signal?: AbortSignal;
} & ([PathOf<O>] extends [never] ? { path?: never } : { path: PathOf<O> });

/** Options are required when the path has parameters, optional otherwise. */
export type OptionsArg<O> = [PathOf<O>] extends [never]
  ? [options?: RequestOptions<O>]
  : [options: RequestOptions<O>];

export function buildUrl(
  template: string,
  path?: Record<string, unknown>,
  query?: Record<string, unknown>,
): string {
  const filled = template.replace(/\{(\w+)\}/g, (_, name: string) => {
    const v = path?.[name];
    if (v === undefined || v === null) throw new Error(`missing path parameter '${name}'`);
    return encodeURIComponent(String(v));
  });
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v === undefined || v === null || v === "") continue;
    for (const item of Array.isArray(v) ? v : [v]) qs.append(k, String(item));
  }
  const s = qs.toString();
  return `${API_BASE}${filled}${s ? `?${s}` : ""}`;
}

async function send(method: Method, url: string, body: unknown, signal?: AbortSignal) {
  const headers: Record<string, string> = { Accept: "application/json" };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";
  try {
    return await fetch(url, {
      method: method.toUpperCase(),
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiError(0, null);
  }
}

/**
 * Writes the app makes on its own, not because the user did something
 * ("seen" and read receipts). In an admin's view tab (V044) they are skipped
 * quietly: the server would refuse them, and the viewer clicked nothing.
 */
const PASSIVE_WRITES: [string, RegExp][] = [
  ["post", /^\/me\/alerts\/seen$/],
  ["post", /^\/me\/inbox\/(seen|desktop)$/],
  ["post", /^\/me\/strategy-watches\/seen$/],
  ["post", /^\/feed\/calls\/[^/]+\/read$/],
];

function passive(method: Method, template: string, body: unknown): boolean {
  if (method === "put" && template === "/ai-live/me") {
    return !!body && typeof body === "object" && "seen" in body && !("email" in body);
  }
  return PASSIVE_WRITES.some(([m, re]) => m === method && re.test(template));
}

function isViewOnly(detail: unknown): boolean {
  return !!detail && typeof detail === "object" && (detail as { code?: unknown }).code === "view_only";
}

export async function request<T>(
  method: Method,
  template: string,
  options: { path?: object; query?: object; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const url = buildUrl(
    template,
    options.path as Record<string, unknown> | undefined,
    options.query as Record<string, unknown> | undefined,
  );
  if (method !== "get" && getView() && passive(method, template, options.body)) return undefined as T;
  // Started by the HTML head before the app loaded, without a token, so only a
  // signed-out visitor's first GET of that URL takes it (src/api/prefetch.ts).
  const signedOut = !getAccessToken() && !hasRefreshToken();
  const early = method === "get" && signedOut ? takePrefetched(url) : undefined;
  if (early) {
    const v = await early;
    if (v != null) return v as T;
  }
  // Token missing or about to expire: refresh before the request, not after a 401.
  if (!getAccessToken() && hasRefreshToken()) await refreshAccess();
  let res = await send(method, url, options.body, options.signal);
  if (res.status === 401 && hasRefreshToken() && (await refreshAccess())) {
    res = await send(method, url, options.body, options.signal);
  }
  if (!res.ok) {
    const err = await errorFrom(res);
    // a view tab tried a change: the banner shows a friendly note (V044)
    if (res.status === 403 && isViewOnly(err.detail) && typeof window !== "undefined")
      window.dispatchEvent(new CustomEvent(VIEW_ONLY_EVENT));
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function method<M extends Method>(m: M) {
  return <P extends PathsFor<M>>(path: P, ...[options]: OptionsArg<Op<P, M>>) =>
    request<ResponseOf<Op<P, M>>>(m, path as string, options as object | undefined);
}

export const api = {
  get: method("get"),
  post: method("post"),
  put: method("put"),
  patch: method("patch"),
  delete: method("delete"),
};
